import { OrderType } from '@/enums/OrderEnums';
import { RoundMode } from '@/enums/RoundMode';
import { computeOrderTotals } from '@/domain/pos/pricing/totals';
import {
  IDeliveryZoneInput,
  resolveDeliveryCost,
} from '@/domain/pos/pricing/delivery-zones';
import type { IPosDraft, IPosOrderTotals } from '@/domain/pos/pos-types';
import { useRestaurantStore } from '@/stores/restaurant.store';

export function emptyPosTotals(): IPosOrderTotals {
  return {
    products: 0,
    delivery: 0,
    productsTax: 0,
    deliveryTax: 0,
    discountedAmount: 0,
    total: 0,
    roundDifference: 0,
  };
}

/**
 * Synchronous draft totals via the ported pricing domain, reading the same
 * settings sources the legacy `use-ordering.orderCostSummary` uses today
 * (restaurant tax/settings + selected branch delivery config + zones).
 */
export function usePosDraftTotals() {
  const restaurantStore = useRestaurantStore();

  function getDraftDeliveryCost(draft: IPosDraft): number {
    if (draft.type !== OrderType.Delivery) return 0;

    const branch = restaurantStore.selectedBranch;
    if (!branch) return 0;

    const zones: IDeliveryZoneInput[] = (
      restaurantStore.deliveryZones ?? []
    ).map((z) => ({
      id: z.id,
      cost: z.cost,
      pointsJson: JSON.stringify(z.points ?? []),
      enabled: z.enabled,
    }));

    return resolveDeliveryCost({
      baseDeliveryCost: branch.deliveryCost ?? 0,
      zonedDelivery: !!branch.zonedDelivery,
      zonedFee: !!branch.zonedFee,
      deliveryInfo: draft.deliveryInfo ?? null,
      deliveryZones: zones,
      strictOutsideZone: false,
    }).deliveryCost;
  }

  function computeDraftTotals(draft: IPosDraft): IPosOrderTotals {
    const restaurant = restaurantStore.restaurant;
    const settings = restaurantStore.restaurantSettings;
    if (!restaurant || !settings) return emptyPosTotals();

    return computeOrderTotals({
      type: draft.type,
      cartItems: draft.cartLines,
      discounts: draft.discounts,
      deliveryCost: getDraftDeliveryCost(draft),
      taxPercent: restaurant.tax ?? null,
      taxMode: settings.taxMode,
      taxDelivery: !!settings.taxDelivery,
      roundMode: settings.roundMode ?? RoundMode.None,
      roundInterval: settings.roundInterval ?? 0,
    });
  }

  return {
    computeDraftTotals,
    getDraftDeliveryCost,
  };
}
